<template>
    <div class="container">
        <v-title :lineage="[{'Value Lists': 'value-lists'}]">
            {{isFormUpdating ? 'Update' : 'Add'}} Value List
            <div slot="actions">
                <router-link
                    v-if="isFormUpdating"
                    class="btn inline-s" :to="{name: 'history-list-item', params: {id, version: list.version}}">
                    Go to History
                </router-link>
                <v-cancel :defaultRoute="{name: 'value-lists'}"></v-cancel>
                <r-button type="primary" @click="save" :loading="isLoading">Save List</r-button>
            </div>
        </v-title>
        <v-inline-notifications
            v-if="repeatMessage"
            type="positive"
            title="Value list successfully created"
            class="stack-l">
            <span slot="content">
                Would you like to add <r-button type="link" @click="newFromContinue">another list</r-button> or go back to <router-link :to="{name: 'value-lists'}">manage lists</router-link>?
            </span>
        </v-inline-notifications>
        <v-error-messages
            :has-frontend-errors="$v.list.$error"
            :server-error="serverError"
            class="stack-l">
            <li class="list-item" v-if="!$v.list.description.required">List description is required</li>
            <li class="list-item" v-if="!$v.list.values.required">List values are required</li>
        </v-error-messages>
        <v-form-tile>
            <h2 slot="title">List Information</h2>
            <p slot="description" class="text-muted">Lists are available in the Rules Engine conditions and allow you to
                share the same information for multiple rules at a time. Define often needed lists of values for
                targeting specific properties of commonly used condition entities like customers, transactions or
                BINs.</p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input
                                label="List Description"
                                v-model="list.description"
                                :validate="$v.list.description"
                                :disabled="isLoading"
                                help-text="As displayed in the rules engine condition selection."
                                class="slug-source"
                            />
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input
                                label="List ID"
                                v-model="list.id"
                                :validate="$v.list.id"
                                :disabled="isLoading || isFormUpdating"
                                help-text="An ID will be automatically generated if you leave this field empty."
                                class="slug-target"
                            />
                        </div>

                    </div>
                </div>
                <div class="tile-content">
                    <div class="tile-subtitle">
                        <h3>List Values</h3>
                        <v-popper class="no-flex" position="bottomEnd" ref="csvUpload" :offset="8">
                            <div slot="trigger">
                                <r-button type="link"
                                    v-popper-trigger
                                    class="no-flex">Upload CSV
                                </r-button>
                            </div>
                            <div class="popover" slot="content">
                                <div class="popover-control">
                                    <div class="popover-content popover-menu">
                                        <v-file-selector
                                            ref="file-selector"
                                            @input="parseFileSource"
                                            @cancel="cancelFileProcess"
                                            @close="closeCSVUploadPopper">
                                            <template slot-scope="{openFileBrowser, setFocus}">
                                                <a
                                                    v-for="option in fileImportOptions"
                                                    class="popover-menu-item"
                                                    href="#"
                                                    @click="openFileBrowser"
                                                    @click.prevent="handleSelectFileImportType(option.value)"
                                                    @focus="setFocus">
                                                    {{ option.label }}
                                                </a>
                                            </template>
                                        </v-file-selector>
                                    </div>
                                </div>
                            </div>
                        </v-popper>
                    </div>
                    <div class="grid-content">
                        <div class="grid-item grid-item-full">
                            <r-input
                                v-if="fileIsReady && canEditValues"
                                label="Values"
                                rows="8"
                                :value="inlineValues"
                                :multiline="true"
                                :validate="$v.list.values"
                                :disabled="isLoading"
                                :help-text="`Add a new line to create a value. Number of values: ${listValuesCount}`"
                                @input="updateValuesByInput"
                            />
                            <div v-else-if="fileIsReady">
                                <ul class="list">
                                    <li class="list-item">Your list holds {{ listValuesCount | number }} values.</li>
                                    <li class="list-item">Large list of {{ maxEditableValues | number }} or more values cannot be
                                        edited manually.</li>
                                </ul>
                            </div>
                            <p v-if="!fileIsReady">
                                Parsing file... Please wait.
                            </p>

                            <v-error-messages class="stack-l">
                                <li v-for="fileError in fileErrors" class="list-item" v-text="fileError"></li>
                            </v-error-messages>
                        </div>
                    </div>
                </div>
                <div class="tile-content" v-if="fileIsReady && !canEditValues">
                    <r-button @click="resetFile">Reset Values</r-button>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <div class="grid-fitted stack-l">
            <div class="grid-item text-right equal-widths-elements-mobile">
                <router-link
                    v-if="isFormUpdating"
                    class="btn inline-s" :to="{name: 'history-list-item', params: {id, version: list.version}}">
                    Go to History
                </router-link>
                <v-cancel :defaultRoute="{name: 'value-lists'}"></v-cancel>
                <r-button type="primary" @click="save" :loading="isLoading">Save List</r-button>
            </div>
        </div>
    </div>
</template>

<script>
    import {required} from 'vuelidate/lib/validators';
    import vTitle from '@/components/v-title';
    import {
        vCancel, vErrorMessages, vFileSelector, vFormTile, vIcon, vSelect,
    } from '@/components/form/index';
    import vPopper from '@/components/v-popper';
    import vInlineNotifications from '@/components/v-inline-notifications';
    import FormLoaderMixin from '@/mixins/form-loader-mixin';
    import sluggifyMixin from '@/mixins/sluggify-mixin';
    import ListModel from '@/common/models/list-model';
    import vCustomFieldsManager from '@/components/custom-fields/v-custom-fields-manager';
    import {createDialog, vConfirm} from '@/components/modal/dialogs/index';
    import AppWorker from '@/common/worker';
    import prepareCSVImportResults from '../helpers/prepare-csv-import-results';
    import mergeCSVImportResultsData from '../helpers/merge-csv-import-results-data';
    import pasteToCommaString from '@/common/helpers/paste-to-comma-string';
    import {importStrategies, maxManualEditableValues, notificationFileSize} from '@/modules/rules-engine/lists/file-import-settings';

    export default {
        name: 'list-form',
        mixins: [
            FormLoaderMixin,
            sluggifyMixin({
                target: 'list.id',
                source: 'list.description',
                entityCallback(id) {
                    return this.$api(({lists}) => lists.get({id}));
                },
            }),
        ],
        components: {
            vTitle,
            vSelect,
            vFileSelector,
            vCancel,
            vIcon,
            vFormTile,
            vErrorMessages,
            vInlineNotifications,
            vCustomFieldsManager,
            vPopper,
        },
        props: {
            id: {
                type: String,
                default: null,
            },
            continue: {
                type: String,
                default: null,
            },
        },
        computed: {
            // returns true if values is showed in the textarea
            canEditValues() {
                return this.listValuesCount <= this.maxEditableValues;
            },
            listValuesCount() {
                return this.list.values.length;
            },
            repeatMessage() {
                return this.continue !== null;
            },
        },
        data() {
            return {
                inlineValues: null,
                list: new ListModel(),
                file: null, // file object
                fileIsReady: true, // file parsed
                fileErrors: [], // file parser errors
                fileImportOptions: [...importStrategies], // file import type dropdown values
                fileImportStrategy: null,
                maxEditableValues: maxManualEditableValues,
            };
        },
        validations: {
            list: {
                description: {required},
                id: {},
                values: {required},
            },
        },
        methods: {
            newFromContinue() {
                this.$v.$reset();
                this.list = new ListModel();
                this.$router.push({name: 'add-list-item'});
            },
            resetFile() {
                this.fileImportStrategy = null;
                this.file = null;
                this.fileIsReady = true;
                this.fileErrors = [];
                this.list.values = [];
                this.inlineValues = null;
                /**
                 * you should reset input file value,
                 * in case if you are changed type and not changed file
                 */
                this.$refs['file-selector'].resetFileValue();
            },
            /**
             * If user select type and then cancel load file
             * you should reset dropdown value
             */
            cancelFileProcess() {
                /**
                 * if user already imported file (or added some values), or it is edit page
                 * and then user click "import", then cancel - you can't reset values
                 * you should leave the previous one
                 */
                if (this.list.values.length) {
                    return;
                }
                this.resetFile();
            },
            /**
             * File import type is selected, next please select the file.
             * @property {string} fileImportStrategy - one value from file-import-settings.js
             */
            handleSelectFileImportType(fileImportStrategy) {
                this.fileImportStrategy = fileImportStrategy;
            },
            closeCSVUploadPopper() {
                this.$refs.csvUpload.$emit('request-popper-hide');
            },
            /**
             * @param {file} fileList - file value
             * @returns {Promise<void>}
             */
            async parseFileSource(fileList) {
                if (!fileList.length) {
                    this.resetFile();
                    return;
                }

                // eslint-disable-next-line
            this.file = fileList[0];
                this.fileIsReady = false;

                // show notification if it's big file
                if (this.file.size > notificationFileSize) {
                    this.$toast.positive(`Starting to parse the file, please wait.`);
                }

                const output = await AppWorker.csv.parse(this.file);

                // valid file, update values
                if (output.isValid) {
                    this.updateValuesByCSVOutput(output);
                    return;
                }

                // invalid file type or etc. need confirm
                const confirmDialog = createDialog(vConfirm, this);
                const dialogParams = {
                    title: 'Wrong File',
                    content: `Selected file ${this.file.name}
                         does not seem to be a valid CSV format.
                         The data might not be read correctly.
                         Do you wish to continue with the import?`,
                    confirmLabel: 'Yes',
                };

                const isConfirmed = await confirmDialog(dialogParams);

                // reset file
                if (!isConfirmed) {
                    this.resetFile();
                    return;
                }

                // update values
                this.updateValuesByCSVOutput(output);
            },
            // update values by user input in textarea
            updateValuesByInput(value) {
                this.inlineValues = value;
                ({values: this.list.values} = pasteToCommaString(value));
            },
            async updateValuesByCSVOutput(output) {
                const results = await prepareCSVImportResults(output);
                const data = await mergeCSVImportResultsData(
                    this.fileImportStrategy,
                    this.list.values,
                    results.data,
                );

                // update values
                this.list.values = data.values;
                this.updateInlineFromValues();

                // notify errors
                this.fileErrors = output.errors;

                if (this.fileErrors.length) {
                    this.$toast.negative(`Found ${this.fileErrors.length} errors`);
                }
                if (data.removed) {
                    this.$toast.warning(`Removed ${data.removed} values`);
                }
                if (data.added) {
                    this.$toast.positive(`Added ${data.added} values`);
                }

                // unlock textarea
                this.fileIsReady = true;
            },
            async save() {
                this.$v.$touch();

                if (!this.$v.list.$error) {
                    this.isLoading = true;

                    try {
                        const params = {
                            data: this.list.getPayload(),
                        };
                        if (this.list.id) {
                            params.id = this.list.id;
                        }

                        const method = this.isFormUpdating ? 'update' : 'create';
                        const list = await this.$api(({lists}) => lists[method](params));

                        /**
                         * Most of params updated by user,
                         * but, some params for example version will returned from the server
                         * so we should to update model with new params
                         * @type {ListModel}
                         */
                        this.list = new ListModel(list.fields);

                        if (!this.isFormUpdating) {
                            window.scrollTo(0, 0);
                            this.$router.push({name: 'edit-list-item', params: {id: list.fields.id, continue: 'continue'}});
                        } else {
                            this.$toast.positive('List successfully updated');
                            this.$router.push({name: 'edit-list-item', params: {id: list.fields.id}});
                        }
                        this.isLoading = false;
                    } catch (error) {
                        this.serverError = error;
                    } finally {
                        // invalidate custom field cache
                        this.$store.dispatch('rulesEngine/invalidateLists');
                        this.isLoading = false;
                    }
                }
            },
            async fetchData() {
                if (!this.isFormUpdating) {
                    return;
                }
                try {
                    const list = await this.$api(({lists}) => lists.get({id: this.id}));
                    this.list = new ListModel(list.fields);
                    this.updateInlineFromValues();
                } catch (error) {
                    this.$redirectOnError(error, {name: 'value-lists'});
                }
            },
            updateInlineFromValues() {
                this.inlineValues = this.list.values.join('\n');
            },
        },
    };
</script>
